<template>
  <div class="room-fee-import-detail-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('roomFeeImportDetail.page.title')" :subtitle="$t('roomFeeImportDetail.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="goBack">
          {{ $t('roomFeeImportDetail.button.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.floorNum" clearable class="w-full"
            :placeholder="$t('roomFeeImportDetail.search.floorNum')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.unitNum" clearable class="w-full"
            :placeholder="$t('roomFeeImportDetail.search.unitNum')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.roomNum" clearable class="w-full"
            :placeholder="$t('roomFeeImportDetail.search.roomNum')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('roomFeeImportDetail.table.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('roomFeeImportDetail.page.empty')" @retry="getList">
        <el-table-column prop="floorNum" :label="$t('roomFeeImportDetail.table.floorNum')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.floorNum || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="unitNum" :label="$t('roomFeeImportDetail.table.unitNum')" align="center" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.unitNum || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="roomNum" :label="$t('roomFeeImportDetail.table.roomNum')" align="center" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.roomNum || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="feeName" :label="$t('roomFeeImportDetail.table.feeName')" align="left" min-width="150"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.feeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="startTime" :label="$t('roomFeeImportDetail.table.startTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.startTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="endTime" :label="$t('roomFeeImportDetail.table.endTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.endTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="amount" :label="$t('roomFeeImportDetail.table.amount')" align="right" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('roomFeeImportDetail.table.remark')" align="left" min-width="140"
          show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="ds-text-muted">{{ scope.row.remark || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('roomFeeImportDetail.table.state')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :type="scope.row.state === '1000' ? 'success' : 'danger'"
              :label="scope.row.state === '1000' ? $t('roomFeeImportDetail.status.success') : $t('roomFeeImportDetail.status.failed')" />
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('roomFeeImportDetail.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>
  </div>
</template>

<script>
import { queryImportFeeDetail } from '@/api/fee/roomFeeImportDetailApi'
import { getCommunityId } from '@/api/community/communityApi'
import { formatDateTimeSec, formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'RoomFeeImportDetail',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        floorNum: '',
        unitNum: '',
        roomNum: '',
        importFeeId: ''
      },
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.initData()
  },
  methods: {
    initData() {
      // 从路由参数获取 importFeeId
      this.searchForm.importFeeId = this.$route.query.importFeeId
      this.getList()
    },
    async getList() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          importFeeId: this.searchForm.importFeeId,
          floorNum: this.searchForm.floorNum,
          unitNum: this.searchForm.unitNum,
          roomNum: this.searchForm.roomNum,
          communityId: getCommunityId()
        }
        const { data, total } = await queryImportFeeDetail(params)
        this.tableData = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('获取导入费用详情失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('roomFeeImportDetail.page.loadFailed')
        this.$message.error(this.$t('roomFeeImportDetail.message.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm.floorNum = ''
      this.searchForm.unitNum = ''
      this.searchForm.roomNum = ''
      this.handleSearch()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    goBack() {
      this.$router.go(-1)
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.room-fee-import-detail-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 辅助文本 */
.ds-text-muted {
  color: var(--ds-gray-500);
}
</style>
